import { createReducer, on } from '@ngrx/store';

import * as ReportActions from './report.actions';
import { Report, ReportObject } from '../models/report.model';
import { Interval } from '../../analysis/models/interval';
import { TimestreamMetric } from '@app/home/models/metric.model';

export const reportFeatureKey = 'report';

export interface State {
  historics: Array<Report>;
  favorites: Array<Report>;
  currentReport: Report;
  generatingReport: Report;
  isPropertyPanel: boolean;
  isChartPanel: boolean;
  isExporting: boolean;
  isPreview: boolean;
  previewReport: Report;
  previewData: Array<TimestreamMetric>;
}

const initialState: State = {
  historics: [],
  favorites: [],
  currentReport: { startDate: null, endDate: null, interval: Interval.THREE_MINUTES, objects: [] },
  generatingReport: { startDate: null, endDate: null, interval: Interval.THREE_MINUTES, objects: [] },
  isPropertyPanel: false,
  isChartPanel: false,
  isExporting: false,
  isPreview: false,
  previewReport: null,
  previewData: []
};

const cleanReport = {
  id: null,
  name: '',
  isFavorite: false,
  lastDownloaded: null,
  lastUpdated: null
};

export const reducer = createReducer(
  initialState,
  on(ReportActions.resetStateApply, state => ({
    ...initialState,
    isExporting: state.isExporting
  })),
  on(ReportActions.resetReport, state => ({
    ...state,
    currentReport: initialState.currentReport,
    isPropertyPanel: false
  })),
  on(ReportActions.loadReportsSuccess, (state, { historics, favorites }) => ({
    ...state,
    historics,
    favorites
  })),
  on(ReportActions.loadReportSuccess, (state, { report }) => ({
    ...state,
    currentReport: { ...report },
    previewReport: { ...report }
  })),
  on(ReportActions.updatePreview, (state, { report }) => ({
    ...state,
    previewReport: { ...report }
  })),
  on(ReportActions.addItems, (state, { items }) => ({
    ...state,
    currentReport: {
      ...state.currentReport,
      ...cleanReport,
      objects: [
        ...state.currentReport.objects,
        ...items.map(item => {
          return {
            key: item.key,
            equipmentType: item.equipmentId,
            name: item.properties.name,
            ouvrage: item.properties.ouvrage,
            available: Object.keys(item.properties).filter(
              key => (key.startsWith('s_') || key.startsWith('t_')) && !isNaN(item.properties[key])
            ),
            properties: []
          } as ReportObject;
        })
      ]
    }
  })),
  on(ReportActions.removeItems, (state, { items }) => ({
    ...state,
    currentReport: {
      ...state.currentReport,
      ...cleanReport,
      objects: [...state.currentReport.objects.filter(item => !items.includes(item))]
    }
  })),
  on(ReportActions.toggleProperties, state => ({
    ...state,
    isPropertyPanel: !state.isPropertyPanel
  })),
  on(ReportActions.updateProperties, (state, { objects }) => ({
    ...state,
    currentReport: {
      ...state.currentReport,
      ...cleanReport,
      startDate: state.previewReport.startDate,
      endDate: state.previewReport.endDate,
      interval: state.previewReport.interval,
      objects
    }
  })),
  on(ReportActions.updateReportList, (state, { report }) => ({
    ...state,
    favorites: [report, ...state.favorites.filter(rep => rep.id !== report.id)]
  })),
  on(ReportActions.removeReport, (state, { report }) => ({
    ...state,
    currentReport: state.currentReport.id === report.id ? initialState.currentReport : state.currentReport,
    historics: [...state.historics.filter(rep => rep.id !== report.id)],
    favorites: [...state.favorites.filter(rep => rep.id !== report.id)]
  })),
  on(ReportActions.generateReport, state => ({
    ...state,
    isExporting: true
  })),
  on(ReportActions.storeReport, (state, { report }) => ({
    ...state,
    generatingReport: report
  })),
  on(ReportActions.generateReportSuccess, (state, { report }) => ({
    ...state,
    isExporting: false,
    historics: [report, ...state.historics.filter(rep => !report.id || rep.id !== report.id)]
  })),
  on(ReportActions.togglePreview, state => ({
    ...state,
    isPreview: !state.isPreview
  })),
  on(ReportActions.loadPreview, (state, { data }) => ({
    ...state,
    previewData: data
  }))
);
